import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Button, Avatar, Tooltip, Tag, Progress, Popover, message } from 'antd';
import { DeleteOutlined, CarOutlined, EyeOutlined, WarningFilled, TeamOutlined, StopOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useDispatch, useSelector } from 'react-redux';
import { batchActions } from '../../actions/batchActions';
import { batchService } from '../../services/batchService';
import GuardedButton from '../../components/guardedButton';
import { useCurrentPageAccess } from '../../services/helper';
import { getblobLink } from '../../services/helper';

const CAL_START = '01:00:00';
const CAL_END = '24:00:00';

const parseTime = (t: string): number => {
    if (!t) return 5;
    const [h, m] = t.split(':').map(Number);
    return h + (m || 0) / 60;
};

const getInstructorName = (batch: any): string => {
    if (!batch) return '';
    return batch.instructorName || (batch.instructorId && typeof batch.instructorId === 'object'
        ? `${batch.instructorId.firstName || ''} ${batch.instructorId.lastName || ''}`.trim()
        : '');
};

const EVENT_COLORS: Record<string, { bg: string; border: string; text: string }> = {
    ACTIVE: { bg: '#e0f0ff', border: '#3689e9', text: '#1a5fa8' },
    SIMULATION: { bg: '#f0e6ff', border: '#7c3aed', text: '#5b21b6' },
    COMPLETED: { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
    INACTIVE: { bg: '#f3f4f6', border: '#9ca3af', text: '#6b7280' },
};

const getBatchColor = (b: any) => {
    if (!b || !b.trainingType) return EVENT_COLORS.INACTIVE;
    if (b.trainingType === 'SIMULATION') return EVENT_COLORS.SIMULATION;
    if (b.status === 'COMPLETED') return EVENT_COLORS.COMPLETED;
    if (b.status === 'ACTIVE') return EVENT_COLORS.ACTIVE;
    return EVENT_COLORS.INACTIVE;
};

const LEGEND = [
    { key: 'ACTIVE', label: 'Active', color: '#3689e9' },
    { key: 'SIMULATION', label: 'Simulation', color: '#7c3aed' },
    { key: 'COMPLETED', label: 'Completed', color: '#22c55e' },
    { key: 'INACTIVE', label: 'Inactive', color: '#9ca3af' },
];



const getStudentList = (batch: any): any[] =>
    Array.isArray(batch?.studentIds) ? batch.studentIds.filter((s: any) => s && typeof s === 'object') : [];

const getStudentCount = (batch: any): number =>
    batch?.studentsCount ?? (Array.isArray(batch?.studentIds) ? batch.studentIds.length : 0);

const getStudentName = (s: any, i: number): string =>
    (s && typeof s === 'object') ? `${s.firstName || ''} ${s.lastName || ''}`.trim() || `Student ${i + 1}` : `Student ${i + 1}`;

const StudentQuickView: React.FC<{ batch: any; onViewAll: () => void }> = ({ batch, onViewAll }) => {
    const list = getStudentList(batch);
    const count = getStudentCount(batch);
    const shown = list.slice(0, 5);
    return (
        <div className="bcal-qv">
            <div className="bcal-qv-head">
                <TeamOutlined />
                <span className="bcal-qv-title">Students</span>
                <span className="bcal-qv-count">{count}{batch.capacity ? ` / ${batch.capacity}` : ''}</span>
            </div>
            {shown.length > 0 ? (
                <div className="bcal-qv-list">
                    {shown.map((s: any, i: number) => {
                        const name = getStudentName(s, i);
                        const isFemale = (s.gender || '').toLowerCase() === 'female';
                        const status = s.attendanceStatus as string | undefined;
                        return (
                            <div key={s._id || i} className="bcal-qv-row">
                                <img src={isFemale ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`} alt="" className="bcal-qv-avatar" />
                                <span className="bcal-qv-name">{name}</span>
                                {status && <span className={`bcal-qv-att ${status === 'PRESENT' ? 'present' : 'absent'}`}>{status === 'PRESENT' ? 'Present' : 'Absent'}</span>}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="bcal-qv-empty">{count > 0 ? `${count} student${count !== 1 ? 's' : ''} enrolled` : 'No students enrolled yet'}</div>
            )}
            {count > shown.length && shown.length > 0 && <div className="bcal-qv-more">+{count - shown.length} more</div>}
            <button type="button" className="bcal-qv-viewall" onClick={onViewAll}>View all details</button>
        </div>
    );
};

interface BatchCalendarViewProps {
    batches: any[];
    instructors: any[];
    onView: (batch: any) => void;
    onDelete: (id: string) => void;
    onAddBatch?: () => void;
}

const MiniCalendar: React.FC<{
    currentDate: Dayjs;
    onDayClick: (d: Dayjs) => void;
}> = ({ currentDate, onDayClick }) => {
    const [miniMonth, setMiniMonth] = useState<Dayjs>(() => dayjs().startOf('month'));

    useEffect(() => {
        setMiniMonth(currentDate.startOf('month'));
    }, [currentDate.format('YYYY-MM')]);

    const startOfGrid = miniMonth.startOf('month').startOf('week');
    const days = Array.from({ length: 42 }, (_, i) => startOfGrid.add(i, 'day'));
    const today = dayjs().format('YYYY-MM-DD');
    const selected = currentDate.format('YYYY-MM-DD');

    return (
        <div className="bcal-mini-cal">
            <div className="bcal-mini-header">
                <button className="bcal-mini-nav" onClick={() => setMiniMonth(p => p.subtract(1, 'month'))}>‹</button>
                <span className="bcal-mini-month">{miniMonth.format('MMMM YYYY')}</span>
                <button className="bcal-mini-nav" onClick={() => setMiniMonth(p => p.add(1, 'month'))}>›</button>
            </div>
            <div className="bcal-mini-grid">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div key={i} className="bcal-mini-dow">{d}</div>
                ))}
                {days.map((day, i) => {
                    const key = day.format('YYYY-MM-DD');
                    const isCurMonth = day.month() === miniMonth.month();
                    const isToday = key === today;
                    const isSelected = key === selected;
                    return (
                        <div
                            key={i}
                            style={{ cursor: 'pointer' }}
                            className={[
                                'bcal-mini-day',
                                !isCurMonth ? 'other-month' : '',
                                isToday ? 'today' : '',
                                isSelected && !isToday ? 'selected-day' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => onDayClick(day)}
                        >
                            {day.date()}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────────── */
const CalendarBatchView: React.FC<BatchCalendarViewProps> = ({ batches, instructors, onView, onDelete, onAddBatch }) => {
    const calendarRef = useRef<FullCalendar>(null);
    const calendarWrapperRef = useRef<HTMLDivElement>(null);
    const [calView, setCalView] = useState<'timeGridDay' | 'timeGridWeek' | 'dayGridMonth'>('timeGridWeek');
    const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
    const [visibleRange, setVisibleRange] = useState<{ start: string; end: string }>({
        start: dayjs().startOf('week').format('YYYY-MM-DD'),
        end: dayjs().startOf('week').add(6, 'day').format('YYYY-MM-DD'),
    });
    const [selected, setSelected] = useState<any | null>(null);
    const [callanderDetails, setCalendarDetails] = useState<any | null>(null);
    const [markedOffMap, setMarkedOffMap] = useState<Map<string, Set<string>>>(new Map());
    const [markedOffSessionsMap, setMarkedOffSessionsMap] = useState<Map<string, Set<string>>>(new Map());
    const [sessionToggleLoading, setSessionToggleLoading] = useState(false);

    const batchCalendarDetails = useSelector((state: any) => state.getCalendarDetailsReducer);
    const dispatch = useDispatch();
    const pageAccess = useCurrentPageAccess();

    const rangeStartStr = visibleRange.start;
    const rangeEndStr = visibleRange.end;
    const validInstructors = useMemo(() => {
        if (!Array.isArray(instructors)) return [];
        return instructors.filter((u: any) => {
            const roleCode = (u?.role?.role || u?.role || '').toString().toUpperCase();
            return roleCode === 'INSTRUCTOR';
        });
    }, [instructors]);

    useEffect(() => {
        dispatch(batchActions.getCalendarDetails({ startDate: rangeStartStr, endDate: rangeEndStr }));
    }, [rangeStartStr, rangeEndStr]);

    useEffect(() => {
        if (batchCalendarDetails?.isSuccess && batchCalendarDetails?.calendarDetails) {
            setCalendarDetails(batchCalendarDetails.calendarDetails.calendar);
        }
    }, [batchCalendarDetails]);

    const fetchMarkedOff = useCallback(() => {
        let cancelled = false;
        batchService.getMarkedOffInstructors({ startDate: rangeStartStr, endDate: rangeEndStr })
            .then((resp: any) => {
                if (cancelled) return;
                const list = resp?.resultObject?.markedOff ?? resp?.markedOff ?? [];
                const map = new Map<string, Set<string>>();
                list.forEach((m: any) => { if (!map.has(m.date)) map.set(m.date, new Set()); map.get(m.date)!.add(String(m.instructorId)); });
                setMarkedOffMap(map);
            })
            .catch(() => { if (!cancelled) setMarkedOffMap(new Map()); });
        batchService.getMarkedOffSessions({ startDate: rangeStartStr, endDate: rangeEndStr })
            .then((resp: any) => {
                if (cancelled) return;
                const list = resp?.resultObject?.markedOff ?? resp?.markedOff ?? [];
                const map = new Map<string, Set<string>>();
                list.forEach((m: any) => { if (!map.has(m.date)) map.set(m.date, new Set()); map.get(m.date)!.add(String(m.batchId)); });
                setMarkedOffSessionsMap(map);
            })
            .catch(() => { if (!cancelled) setMarkedOffSessionsMap(new Map()); });
        return () => { cancelled = true; };
    }, [rangeStartStr, rangeEndStr]);

    useEffect(() => fetchMarkedOff(), [fetchMarkedOff]);
    useEffect(() => {
        const handleResize = () => {
            const api = calendarRef.current?.getApi();
            if (api) {
                requestAnimationFrame(() => {
                    api.updateSize();
                });
            }
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('sidebar-toggle', handleResize);
        const resizeObserver = new ResizeObserver(() => {
            handleResize();
        });
        if (calendarWrapperRef.current) {
            resizeObserver.observe(calendarWrapperRef.current);
        }
        const timeoutId = setTimeout(handleResize, 100);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('sidebar-toggle', handleResize);
            resizeObserver.disconnect();
            clearTimeout(timeoutId);
        };
    }, []);
    useEffect(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            requestAnimationFrame(() => {
                api.updateSize();
            });
        }
    }, [calView]);

    const isMarkedOff = useCallback((batch: any, dateStr: string): boolean => {
        const sessionSet = markedOffSessionsMap.get(dateStr);
        if (sessionSet?.has(String(batch?._id))) return true;
        const instId = batch?.instructorId?._id || batch?.instructorId;
        if (!instId) return false;
        return markedOffMap.get(dateStr)?.has(String(instId)) ?? false;
    }, [markedOffMap, markedOffSessionsMap]);

    const handleToggleSession = useCallback((batchId: string, dateStr: string) => {
        setSessionToggleLoading(true);
        batchService.markBatchSession({ batchId, date: dateStr })
            .then((resp: any) => {
                fetchMarkedOff();
                dispatch(batchActions.getCalendarDetails({ startDate: rangeStartStr, endDate: rangeEndStr }));
                const msg = resp?.message || resp?.errorMessage;
                if (resp?.errorMessage) message.error(msg);
                else message.success(msg || 'Session updated');
            })
            .catch(() => message.error('Could not update session'))
            .finally(() => setSessionToggleLoading(false));
    }, [fetchMarkedOff, dispatch, rangeStartStr, rangeEndStr]);


    const calendarDataMap = useMemo(() => {
        const map = new Map<string, { studentCount: number; students: any[]; instructorName: string; maxStudents: number }>();
        if (!Array.isArray(callanderDetails)) return map;
        callanderDetails.forEach((dayEntry: any) => {
            (dayEntry.batches || []).forEach((b: any) => {
                const instObj = validInstructors?.find((ins: any) => ins._id === b.instructorId);
                const instructorName = instObj
                    ? `${instObj.firstName || ''} ${instObj.lastName || ''}`.trim()
                    : b.instructorName || '';
                map.set(`${String(b.batchId)}_${dayEntry.date}`, {
                    studentCount: b.studentCount ?? 0,
                    students: Array.isArray(b.students) ? b.students : [],
                    instructorName,
                    maxStudents: b.maxStudents ?? 0,
                });
            });
        });
        return map;
    }, [callanderDetails, validInstructors]);

    const getCalData = useCallback((batch: any, dateStr?: string) => {
        if (dateStr) {
            const keyed = calendarDataMap.get(`${String(batch?._id)}_${dateStr}`);
            if (keyed) return keyed;
        }
        const prefix = `${String(batch?._id)}_`;
        for (const [k, v] of calendarDataMap.entries()) {
            if (k.startsWith(prefix)) return v;
        }
        return null;
    }, [calendarDataMap]);

    const getCalStudentCount = useCallback((batch: any, dateStr?: string) => {
        const cal = getCalData(batch, dateStr);
        return cal ? cal.studentCount : getStudentCount(batch);
    }, [getCalData]);

    const getCalStudentList = useCallback((batch: any, dateStr?: string) => {
        const cal = getCalData(batch, dateStr);
        return cal?.students.length ? cal.students : getStudentList(batch);
    }, [getCalData]);

    const getCalInstructorName = useCallback((batch: any, dateStr?: string) => {
        const cal = getCalData(batch, dateStr);
        return cal?.instructorName || getInstructorName(batch);
    }, [getCalData]);

    const getCalMaxStudents = useCallback((batch: any, dateStr?: string) => {
        const cal = getCalData(batch, dateStr);
        return cal?.maxStudents || batch?.maxStudents || 3;
    }, [getCalData]);

    const instCounts = useMemo(() => {
        const c: Record<string, number> = {};
        batches.forEach(b => { const id = b.instructorId?._id || b.instructorId; if (id) c[id] = (c[id] || 0) + 1; });
        return c;
    }, [batches]);

    const fcEvents = useMemo(() => {
        const events: any[] = [];
        const seenKeys = new Set<string>();

        let d = dayjs(rangeStartStr);
        const end = dayjs(rangeEndStr);
        while (d.isSame(end, 'day') || d.isBefore(end, 'day')) {
            const dateStr = d.format('YYYY-MM-DD');
            batches.forEach(b => {
                const key = `${b._id}_${dateStr}`;
                if (seenKeys.has(key)) return; // ← Prevent duplicate event IDs
                seenKeys.add(key);

                const markedOff = isMarkedOff(b, dateStr);
                const cfg = getBatchColor(b);
                // const isConflict = conflictIds.has(b._id);
                events.push({
                    id: key,
                    title: b.batchName,
                    start: `${dateStr}T${b.startTime}`,
                    end: `${dateStr}T${b.endTime}`,
                    maxStudents: b.maxStudents ?? 3,
                    backgroundColor: markedOff ? '#d9d9d9' : cfg.bg,
                    borderColor: markedOff ? '#bfbfbf' :  cfg.border,
                    textColor: markedOff ? '#8c8c8c' : cfg.text,
                    extendedProps: { batch: b, dateStr, markedOff },
                });
            });
            d = d.add(1, 'day');
        }
        return events;
    }, [batches, rangeStartStr, rangeEndStr, isMarkedOff]);

    const sidebarDateStr = currentDate.format('YYYY-MM-DD');
    const dayViewBatches = useMemo(() =>
        [...batches].filter(b => !isMarkedOff(b, sidebarDateStr))
            .sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime)),
        [batches, sidebarDateStr, isMarkedOff]);

    const offSessions = useMemo(() => {
        const set = markedOffSessionsMap.get(sidebarDateStr);
        if (!set || set.size === 0) return [];
        return batches.filter(b => set.has(String(b._id)))
            .sort((a, b) => parseTime(a.startTime) - parseTime(b.startTime));
    }, [batches, markedOffSessionsMap, sidebarDateStr]);

    const prevPeriod = () => { calendarRef.current?.getApi().prev(); };
    const nextPeriod = () => { calendarRef.current?.getApi().next(); };
    const goToday = () => { calendarRef.current?.getApi().today(); };
    const switchView = (v: typeof calView) => { setCalView(v); calendarRef.current?.getApi().changeView(v); };

    const rangeLabel = useMemo(() => {
        if (calView === 'timeGridDay') return currentDate.format('dddd, MMMM D, YYYY');
        if (calView === 'timeGridWeek') {
            const s = currentDate.startOf('week'), e = s.add(6, 'day');
            return s.month() === e.month()
                ? `${s.format('MMMM')} ${s.date()} – ${e.date()}, ${s.year()}`
                : `${s.format('MMM D')} – ${e.format('MMM D')}, ${s.year()}`;
        }
        return currentDate.format('MMMM YYYY');
    }, [calView, currentDate]);

    const sessionDate = useMemo(() => dayjs(selected?.dateStr || currentDate.format('YYYY-MM-DD')), [selected, currentDate]);
    const sessionDateStr = sessionDate.format('YYYY-MM-DD');
    const isSessionDateTodayOrFuture = sessionDate.isSame(dayjs(), 'day') || sessionDate.isAfter(dayjs(), 'day');

    /* ── Detail Panel ── */
    const renderDetailPanel = () => {
        if (!selected) return null;
        const selectedBatch = selected.batch;
        const calData = getCalData(selectedBatch, sessionDateStr);
        const instName = calData?.instructorName || getInstructorName(selectedBatch);
        const count = calData?.studentCount ?? getStudentCount(selectedBatch);
        const stuList = calData?.students ?? getStudentList(selectedBatch);
        const maxStudents = getCalMaxStudents(selectedBatch, sessionDateStr);
        const instId = selectedBatch.instructorId?._id || selectedBatch.instructorId;
        const instBatches = batches.filter(b => (b.instructorId?._id || b.instructorId) === instId).length;
        const isSessionOff = markedOffSessionsMap.get(sessionDateStr)?.has(String(selectedBatch._id)) ?? false;
        const isInstOff = (() => {
            const iid = selectedBatch.instructorId?._id || selectedBatch.instructorId;
            return iid ? (markedOffMap.get(sessionDateStr)?.has(String(iid)) ?? false) : false;
        })();
        const rosterHidden = isSessionOff || isInstOff;

        return (
            <div className="bcal-detail-panel">
                <div className="bcal-detail-top">
                    <div className="bcal-detail-tags">
                        {selectedBatch.trainingType === 'SIMULATION' && <Tag color="purple">Simulation</Tag>}
                        {selectedBatch.trainingType === 'ON_ROAD' && <Tag color="blue">On Road</Tag>}
                        {/* {conflictIds.has(selectedBatch._id) && <Tag color="orange">Conflict</Tag>} */}
                    </div>
                    <button className="bcal-detail-close" onClick={() => setSelected(null)}>✕</button>
                </div>
                <div className="bcal-detail-name">{selectedBatch.batchName}</div>
                <div className="bcal-detail-sub">
                    {selectedBatch.trainingType === 'SIMULATION' ? 'On Road · Simulation'
                        : selectedBatch.trainingType === 'ON_ROAD' ? 'On Road' : 'General'}
                </div>
                <div className="bcal-detail-time-row">
                    <ClockCircleOutlined />
                    <span className="bcal-detail-time">{selectedBatch.startTime} – {selectedBatch.endTime}</span>
                    <Tag color={selectedBatch.status === 'ACTIVE' ? 'blue' : selectedBatch.status === 'COMPLETED' ? 'green' : 'default'} className="bcal-detail-status-tag">
                        {selectedBatch.status}
                    </Tag>
                </div>

                {isSessionOff && (
                    <div className="bcal-detail-markedoff-alert" role="alert" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fff7e6', border: '1px solid #ffd591', color: '#ad6800', borderRadius: 8, padding: '10px 12px', margin: '12px 0' }}>
                        <WarningFilled style={{ color: '#fa8c16', marginTop: 2 }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>No session on {sessionDate.format('ddd, MMM D, YYYY')}</div>
                            <div style={{ fontSize: 12 }}>This session is marked off for this date.</div>
                        </div>
                    </div>
                )}
                {isInstOff && !isSessionOff && (
                    <div className="bcal-detail-markedoff-alert" role="alert" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fff7e6', border: '1px solid #ffd591', color: '#ad6800', borderRadius: 8, padding: '10px 12px', margin: '12px 0' }}>
                        <WarningFilled style={{ color: '#fa8c16', marginTop: 2 }} />
                        <div>
                            <div style={{ fontWeight: 600 }}>No session on {sessionDate.format('ddd, MMM D, YYYY')}</div>
                            <div style={{ fontSize: 12 }}>{instName || 'The instructor'} is marked off for this date.</div>
                        </div>
                    </div>
                )}


                {instName && (() => {
                    const instObj = typeof selectedBatch.instructorId === 'object' ? selectedBatch.instructorId : null;
                    const isFemale = (instObj?.gender || '').toLowerCase() === 'female';
                    return (
                        <div className="bcal-detail-instructor">
                            <div className="bcal-detail-section-label">INSTRUCTOR</div>
                            <div className="bcal-detail-inst-row">
                                <img src={isFemale ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`} alt={instName} className="bcal-inst-photo" />
                                <div className="bcal-inst-text">
                                    <div className="bcal-detail-inst-name">{instName}</div>
                                    <div className="bcal-detail-inst-meta">
                                        <span className="bcal-avail-dot" />
                                        Available · {instBatches} batch{instBatches !== 1 ? 'es' : ''} today
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {!rosterHidden && (
                    <div className="bcal-detail-stats">
                        <div className="bcal-stat-card">
                            <div className="bcal-stat-lbl">Students</div>
                            <div className="bcal-stat-val">{count}</div>
                            <div className="bcal-stat-cap">/ {maxStudents} capacity</div>
                        </div>
                        {selectedBatch.vehicleId && (
                            <div className="bcal-stat-card">
                                <div className="bcal-stat-lbl">Vehicle</div>
                                <div className="bcal-stat-vehicle-name">{selectedBatch.vehicleId?.vehicleName || '—'}</div>
                                <div className="bcal-stat-vehicle-plate">
                                    <CarOutlined style={{ marginRight: 4 }} />{selectedBatch.vehicleId?.registrationNumber || ''}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {rosterHidden && selectedBatch.vehicleId && (
                    <div className="bcal-detail-stats">
                        <div className="bcal-stat-card">
                            <div className="bcal-stat-lbl">Vehicle</div>
                            <div className="bcal-stat-vehicle-name">{selectedBatch.vehicleId?.vehicleName || '—'}</div>
                            <div className="bcal-stat-vehicle-plate">
                                <CarOutlined style={{ marginRight: 4 }} />{selectedBatch.vehicleId?.registrationNumber || ''}
                            </div>
                        </div>
                    </div>
                )}

                {!rosterHidden && stuList.length > 0 && (
                    <div className="bcal-detail-students">
                        <div className="bcal-detail-section-label">ENROLLED STUDENTS</div>
                        {stuList.slice(0, 5).map((stu: any, i: number) => {
                            const isObj = typeof stu === 'object' && stu !== null && (stu.firstName || stu.lastName);
                            const stuName = isObj ? `${stu.firstName || ''} ${stu.lastName || ''}`.trim() : `Student ${i + 1}`;
                            const stuInit = stuName.split(' ').map((n: string) => n?.[0] || '').join('').toUpperCase();
                            const progress = isObj && stu.progress != null ? stu.progress : null;
                            const isFemStu = (isObj ? (stu.gender || '') : '').toLowerCase() === 'female';
                            const stuAvatar = isObj && stu.gender ? (isFemStu ? `${getblobLink()}/public/asset/female-avatar.png` : `${getblobLink()}/public/asset/male-avatar.png`) : null;
                            const attendanceMarked = isObj ? stu.attendanceMarkedToday : false;
                            const attendanceStatus = isObj ? (stu.attendanceStatus as string | undefined) : undefined;
                            return (
                                <div key={stu._id || i} className="bcal-student-row">
                                    {stuAvatar
                                        ? <img src={stuAvatar} alt={stuName} className="bcal-student-photo" />
                                        : <Avatar src={`${getblobLink()}/public/asset/male-avatar.png`} size={36} className="bcal-student-avatar">{stuInit || 'S'}</Avatar>}
                                    <div className="bcal-student-info">
                                        <span className="bcal-student-name">{stuName}</span>
                                        {progress != null && (
                                            <div className="bcal-student-progress-row">
                                                <Progress percent={progress} size="small" showInfo={false} strokeColor="#3689e9" trailColor="#e8ecf0" style={{ flex: 1, margin: 0 }} />
                                                <span className="bcal-student-pct">{progress}%</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="right">
                                        {attendanceMarked && attendanceStatus ? (
                                            <Tag style={{ border: '1px solid' }} color={attendanceStatus === 'PRESENT' ? 'success' : 'error'}>
                                                {attendanceStatus === 'PRESENT' ? 'Present' : 'Absent'}
                                            </Tag>
                                        ) : (
                                            <Tag style={{ border: '1px solid' }} color="default">Not Marked</Tag>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {count > 5 && <div className="bcal-students-more">+{count - 5} more students</div>}
                    </div>
                )}

                {selectedBatch?.notes && (
                    <div className="bcal-detail-notes">
                        <div className="bcal-detail-section-label">NOTES</div>
                        <div className="bcal-notes-text">{selectedBatch.notes}</div>
                    </div>
                )}
                <div className="bcal-detail-actions">
                    {isSessionDateTodayOrFuture && (
                        <Tooltip title={`${isSessionOff ? 'Restore' : 'Hide'} this session on ${sessionDate.format('MMM D, YYYY')}`}>
                            <Button
                                className="bcal-action-mark-session"
                                loading={sessionToggleLoading}
                                icon={isSessionOff ? <CheckCircleOutlined /> : <StopOutlined />}
                                style={isSessionOff ? { color: '#389e0d', borderColor: '#b7eb8f' } : { color: '#d46b08', borderColor: '#ffd591' }}
                                onClick={() => handleToggleSession(selectedBatch._id, sessionDateStr)}>
                                {isSessionOff ? 'Restore Session' : 'Mark Session Off'}
                            </Button>
                        </Tooltip>
                    )}
                    {pageAccess.isView && isSessionDateTodayOrFuture && (
                        <Button type="primary" className="bcal-action-view" onClick={() => onView(selectedBatch)}>
                            <EyeOutlined /> View
                        </Button>
                    )}
                    {isSessionDateTodayOrFuture && (
                        <GuardedButton danger className="bcal-action-delete" onClick={() => { onDelete(selectedBatch._id); setSelected(null); }}>
                            <DeleteOutlined /> Delete
                        </GuardedButton>
                    )}
                </div>
            </div>
        );
    };

    const renderEventContent = (eventInfo: any) => {
        const { batch, isConflict, dateStr, markedOff } = eventInfo.event.extendedProps;
        const count = getCalStudentCount(batch, dateStr);
        const name = getCalInstructorName(batch, dateStr);

        if (calView === 'dayGridMonth') {
            return (
                <div
                    style={{
                        background: 'rgb(224, 240, 255)',
                        color: '#1a5fa8',
                        borderRadius: 3,
                        padding: '1px 4px',
                        fontSize: 10,
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        lineHeight: 1.4,
                    }}
                    title={batch.batchName}
                >
                    {isConflict && <WarningFilled style={{ fontSize: 9, color: '#f59e0b', flexShrink: 0 }} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{batch.batchName}</span>
                </div>
            );
        }

        const durationMs = eventInfo.event.end && eventInfo.event.start
            ? eventInfo.event.end.getTime() - eventInfo.event.start.getTime()
            : 3600000;
        const durationMins = durationMs / 60000;
        const compact = durationMins < 30;
        const tiny = durationMins < 15;

        const timeDisplay = batch.startTime && batch.endTime
            ? `${batch.startTime} – ${batch.endTime}`
            : (eventInfo.timeText || '');

        const hoverCard = (
            <div style={{ minWidth: 180, maxWidth: 260, height: 'auto', padding: '8px 4px' }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {batch.batchName}
                </div>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 2 }}>
                    {timeDisplay}
                </div>
                {name && (
                    <div style={{ fontSize: 12, color: '#666', wordBreak: 'break-word', whiteSpace: 'normal' }}>{name}</div>
                )}
                {!markedOff && count > 0 && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                        <TeamOutlined style={{ marginRight: 4 }} />{count} student{count !== 1 ? 's' : ''}
                    </div>
                )}
                {markedOff && (
                    <div style={{ fontSize: 11, color: '#d46b08', marginTop: 4 }}>
                        <StopOutlined style={{ marginRight: 4 }} />Session marked off
                    </div>
                )}
            </div>
        );

        const inner = (
            <div className="bcal-event-inner" style={{
                height: '100%',
                padding: tiny ? '1px 4px' : '2px 4px',
                overflow: 'hidden',
                minHeight: 18,
                display: 'flex',
                flexDirection: tiny ? 'row' : 'column',
                alignItems: tiny ? 'center' : 'flex-start',
                gap: tiny ? 4 : 0,
            }}>
                <div style={{
                    fontWeight: 600,
                    fontSize: tiny ? 11 : 12,
                    whiteSpace: tiny ? 'nowrap' : 'normal',
                    wordBreak: tiny ? undefined : 'break-word',
                    overflow: tiny ? 'hidden' : undefined,
                    textOverflow: tiny ? 'ellipsis' : undefined,
                    flex: tiny ? 1 : undefined,
                    minWidth: 0,
                    lineHeight: 1.3,
                }}>
                    {batch.batchName}
                </div>
                {!markedOff && tiny && count > 0 && (
                    <span style={{ fontSize: 10, opacity: 0.8, whiteSpace: 'nowrap' }}>
                        <TeamOutlined /> {count}
                    </span>
                )}
                {!tiny && (
                    <div className="bcal-event-badges" style={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        {!markedOff && count > 0 && (
                            <Popover
                                trigger={['hover', 'click']}
                                placement="right"
                                rootClassName="bcal-qv-pop"
                                content={<StudentQuickView
                                    batch={{ ...batch, studentIds: getCalStudentList(batch, dateStr), studentsCount: count }}
                                    onViewAll={() => setSelected({ batch, dateStr })}
                                />}
                            >
                                <button type="button" className="bcal-event-students" style={{ fontSize: 11 }} onClick={e => e.stopPropagation()}>
                                    <TeamOutlined /> {count}
                                </button>
                            </Popover>
                        )}
                    </div>
                )}
                {!compact && timeDisplay && (
                    <div style={{ fontSize: 11 }}>
                        {timeDisplay}
                    </div>
                )}
                {!compact && name && (
                    <div className="bcal-event-inst" style={{ fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.3 }}>
                        {name}
                    </div>
                )}
            </div>
        );

        if (tiny) {
            return (
                <Popover
                    content={hoverCard}
                    placement="topLeft"
                    trigger="hover"
                    mouseEnterDelay={0.15}
                    overlayStyle={{ zIndex: 9999 }}
                >
                    {inner}
                </Popover>
            );
        }

        return inner;
    };

    return (
        <>
            <style>{`
                .fc .fc-timegrid-slot { border-top: none !important; border-bottom: none !important; }
                .fc .fc-timegrid-slot-minor { border-top: none !important; border-bottom: none !important; }
                .fc .fc-timegrid-slot-lane:not(.fc-timegrid-slot-minor) { border-top: 1px solid #f0f0f0 !important; }
                .fc td, .fc th { border-style: solid !important; }
                .fc .fc-timegrid-col-frame { border: none !important; }
                .fc .fc-timegrid-col { border-bottom: none !important; }
                .fc .fc-timegrid-cols td { border-left: 1px solid #f0f0f0 !important; }
                .fc .fc-timegrid-divider { display: none !important; }
                .fc .fc-daygrid-day { border: 1px solid #f0f0f0 !important; }
                .fc .fc-scrollgrid-section > td { border: none !important; }
                .fc .fc-scrollgrid { border: none !important; }
                .fc .fc-scrollgrid-section-header > td { border-bottom: 1px solid #e5e7eb !important; }
                .fc .fc-daygrid-day-number { color: #000 !important; font-weight: 600; text-decoration: none !important; }
                .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number { color: #3689e9 !important; }
                .fc .fc-daygrid-event { margin: 1px 2px !important; border-radius: 3px !important; border: none !important; }
                .fc .fc-daygrid-event-harness { margin-top: 1px !important; }
                .fc .fc-more-link { font-size: 10px !important; color: #6b7280 !important; }
                .fc .fc-timegrid-col-events { overflow: visible !important; }
                .fc .fc-timegrid-event-harness { overflow: visible !important; transition: z-index 0ms; }
              
            `}</style>
            <div className="bcal-root">
                <div className="bcal-sidebar">
                    <MiniCalendar currentDate={currentDate} onDayClick={(d) => {
                        const api = calendarRef.current?.getApi();
                        if (!api) return; api.gotoDate(d.toDate()); setCurrentDate(d);
                    }} />

                    {validInstructors && validInstructors.length > 0 && (
                        <div className="bcal-trainers">
                            <div className="bcal-section-title">Trainers</div>
                            {validInstructors.slice(0, 6).map((inst: any) => {
                                const id = inst._id;
                                const name = `${inst.firstName} ${inst.lastName}`;
                                const cnt = instCounts[id] || 0;
                                const ini = ((inst.firstName?.[0] || '') + (inst.lastName?.[0] || '')).toUpperCase();
                                return (
                                    <div key={id} className="bcal-trainer-item">
                                        <Avatar size={32} className="bcal-trainer-avatar">{ini}</Avatar>
                                        <div className="bcal-trainer-info">
                                            <div className="bcal-trainer-name">{name}</div>
                                            <div className="bcal-trainer-cnt">{cnt} batch{cnt !== 1 ? 'es' : ''}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <div className="bcal-today-list">
                        <div className="bcal-today-header">
                            <span className="bcal-today-title">{calView === 'timeGridDay' ? currentDate.format('ddd, MMM D') : 'Today'}</span>
                            {dayViewBatches.length > 0 && <span className="bcal-today-remaining">{dayViewBatches.length} Batch{dayViewBatches.length > 1 ? "es" : ""}</span>}
                        </div>
                        {dayViewBatches.length === 0
                            ? <div className="bcal-today-empty">No batches</div>
                            : dayViewBatches.map(b => {
                                const cfg = getBatchColor(b);
                                const instName = getCalInstructorName(b, sidebarDateStr);
                                return (
                                    <div key={b._id} className="bcal-today-item" onClick={() => setSelected({ batch: b, dateStr: sidebarDateStr , maxStudents: b.maxStudents ?? 3})}>
                                        <div className="bcal-today-time-col">{b.startTime}</div>
                                        <div className="bcal-today-dot" style={{ background: cfg.border }} />
                                        <div className="bcal-today-info">
                                            <div className="bcal-today-name">{b.batchName}</div>
                                            {instName && <div className="bcal-today-inst">{instName}</div>}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>

                    {offSessions.length > 0 && (
                        <div className="bcal-offday-list">
                            <div className="bcal-today-header">
                                <span className="bcal-today-title">Off {calView === 'timeGridDay' ? currentDate.format('ddd, MMM D') : 'today'}</span>
                                <span className="bcal-today-remaining">{offSessions.length} off</span>
                            </div>
                            {offSessions.map(b => (
                                <div key={b._id} className="bcal-offday-item">
                                    <div className="bcal-offday-info">
                                        <div className="bcal-offday-name">{b.batchName}</div>
                                        <div className="bcal-offday-time">{b.startTime} – {b.endTime}</div>
                                    </div>
                                    <Button type="link" size="small" className="bcal-offday-restore" icon={<CheckCircleOutlined />} loading={sessionToggleLoading} onClick={() => handleToggleSession(b._id, sidebarDateStr)}>Restore</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bcal-main" ref={calendarWrapperRef}>
                    <div className="bcal-toolbar">
                        <div className="bcal-toolbar-left">
                            <button className="bcal-nav-btn" onClick={prevPeriod}>‹</button>
                            <button className="bcal-nav-btn" onClick={nextPeriod}>›</button>
                            <span className="bcal-range-label">{rangeLabel}</span>
                        </div>
                        <div className="bcal-toolbar-right">
                            <div className="bcal-legend">
                                {LEGEND.map(l => (
                                    <span key={l.key} className="bcal-legend-item">
                                        <span className="bcal-legend-dot" style={{ background: l.color }} />{l.label}
                                    </span>
                                ))}
                            </div>
                          
                            <div className="bcal-view-toggle">
                                {([['timeGridDay', 'Day'], ['timeGridWeek', 'Week'], ['dayGridMonth', 'Month']] as const).map(([v, label]) => (
                                    <button key={v} className={`bcal-view-btn${calView === v ? ' active' : ''}`} onClick={() => switchView(v as typeof calView)}>{label}</button>
                                ))}
                            </div>
                            <button className="bcal-today-btn" onClick={goToday}>Today</button>
                            {onAddBatch && pageAccess.isAdd && (
                                <GuardedButton type="primary" onClick={onAddBatch} style={{ borderRadius: 7, fontWeight: 600, fontSize: 13, height: 28, padding: '0 12px' }}>+ Add Batch</GuardedButton>
                            )}
                        </div>
                    </div>

                    <FullCalendar
                        ref={calendarRef}
                        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                        initialView={calView}
                        headerToolbar={false}
                        events={fcEvents}
                        slotMinTime={CAL_START}
                        slotMaxTime={CAL_END}
                        slotDuration="00:20:00"
                        slotLabelInterval="01:00:00"
                        allDaySlot={false}
                        nowIndicator={true}
                        height="100%"
                        expandRows={false}
                        dayMaxEvents={true}
                        eventContent={renderEventContent}
                        eventClick={(info: any) => {
                            const { batch, dateStr } = info.event.extendedProps;
                            setSelected({ batch, dateStr });
                        }}
                        dayCellContent={(arg: any) => {
                            const isToday = dayjs(arg.date).isSame(dayjs(), 'day');
                            return (
                                <span style={{
                                    color: isToday ? '#3689e9' : '#000',
                                    fontWeight: 600,
                                    fontSize: 13,
                                }}>
                                    {arg.dayNumberText}
                                </span>
                            );
                        }}
                        dayHeaderContent={(arg: any) => {
                            const isToday = dayjs(arg.date).isSame(dayjs(), 'day');
                            if (calView === 'dayGridMonth') {
                                return (
                                    <span style={{ color: '#1b1b1b', fontWeight: 600 }}>
                                        {dayjs(arg.date).format('ddd').toUpperCase()}
                                    </span>
                                );
                            }
                            const dayName = dayjs(arg.date).format('ddd').toUpperCase();
                            const dayNum = dayjs(arg.date).date();
                            return (
                                <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    <span style={{ fontSize: 11, fontWeight: 500, color: isToday ? undefined : '#555' }}>{dayName}</span>
                                    <span style={{
                                        fontSize: 20, fontWeight: 700,
                                        color: isToday ? '#fff' : '#1b1b1b',
                                        background: isToday ? '#3689e9' : 'transparent',
                                        borderRadius: '50%',
                                        width: 32, height: 32,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{dayNum}</span>
                                </span>
                            );
                        }}
                        slotLabelContent={(arg: any) => {
                            const text: string = arg.text || '';
                            const parts = text.split(/(am|pm)/i);
                            return (
                                <span style={{ fontSize: 10, color: '#888' }}>
                                    {parts.map((p, i) =>
                                        /^(am|pm)$/i.test(p)
                                            ? <strong key={i} style={{ fontWeight: 700, marginLeft: 2 }}>{p.toUpperCase()}</strong>
                                            : <span key={i}>{p}</span>
                                    )}
                                </span>
                            );
                        }}
                        datesSet={(arg: any) => {
                            const startLocal = dayjs(arg.start);
                            const endExclusiveLocal = dayjs(arg.end);
                            const start = startLocal.format('YYYY-MM-DD');
                            const end = endExclusiveLocal.subtract(1, 'day').format('YYYY-MM-DD');
                            setVisibleRange({ start, end });
                            const totalDays = endExclusiveLocal.diff(startLocal, 'day');
                            const mid = startLocal.add(Math.floor(totalDays / 2), 'day');
                            setCurrentDate(mid);
                        }}
                    />
                </div>

                {renderDetailPanel()}
            </div>
        </>
    );
};

export default CalendarBatchView;
